import { useState, useRef, useEffect } from "react";
import { Table } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import {
  RiArrowDownSLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "@remixicon/react";
import { ViewMode } from "@/screens/creatives/types";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Computes which page numbers (and ellipsis markers) to display.
 * Rules:
 *  - First and last page are always visible.
 *  - Current page and its immediate neighbours (current ± 1) are visible.
 *  - At the edges the window slides so we always show 3 consecutive pages.
 *  - Gaps of more than 1 between visible pages are replaced by an ellipsis.
 */
export function getVisiblePages(
  currentPage: number,
  totalPages: number,
): (number | "ellipsis")[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set<number>();

  pages.add(1);
  pages.add(totalPages);

  if (currentPage <= 2) {
    pages.add(1);
    pages.add(2);
    pages.add(3);
  } else if (currentPage >= totalPages - 1) {
    pages.add(totalPages - 2);
    pages.add(totalPages - 1);
    pages.add(totalPages);
  } else {
    pages.add(currentPage - 1);
    pages.add(currentPage);
    pages.add(currentPage + 1);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result: (number | "ellipsis")[] = [];

  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push("ellipsis");
    }
    result.push(sorted[i]);
  }

  return result;
}

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  buttonClass?: string;
  wrapperClassName?: string;
  className?: string;
  viewMode?: ViewMode;
  isUserInitiatedPaginationChanges?: React.MutableRefObject<boolean>;
  paginationTitle?: string;
  userPageSize?: number;
  hidePageSizeSelector?: boolean;
}

export function DataTablePagination<TData>({
  table,
  wrapperClassName,
  className,
  viewMode,
  isUserInitiatedPaginationChanges,
  paginationTitle,
  userPageSize,
  hidePageSizeSelector = false,
}: DataTablePaginationProps<TData>) {
  const currentPageSize = table.getState().pagination.pageSize;
  const displayPageSize = userPageSize ?? currentPageSize;
  const pageSizeOptions = viewMode === "line_chart_view" ? [4, 6, 8] : [10, 30, 50, 100];

  const currentPage = table.getState().pagination.pageIndex + 1; // 1-based
  const totalPages = table.getPageCount();

  // displayedPage drives the pagination UI independently of the table
  // so the spring animation can finish before the heavy table re-render fires.
  const [displayedPage, setDisplayedPage] = useState(currentPage);
  const deferredUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (deferredUpdateRef.current) {
      clearTimeout(deferredUpdateRef.current);
      deferredUpdateRef.current = null;
    }
    setDisplayedPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    return () => {
      if (deferredUpdateRef.current) clearTimeout(deferredUpdateRef.current);
    };
  }, []);

  const visiblePages = getVisiblePages(displayedPage, totalPages);

  const navigateToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setDisplayedPage(page);
    if (deferredUpdateRef.current) clearTimeout(deferredUpdateRef.current);
    deferredUpdateRef.current = setTimeout(() => {
      deferredUpdateRef.current = null;
      if (isUserInitiatedPaginationChanges)
        isUserInitiatedPaginationChanges.current = true;
      table.setPageIndex(page - 1);
    }, 200);
  };

  if (totalPages <= 1) return null;

  return (
    <div className={cn("flex items-center px-0 py-2", wrapperClassName)}>
      <div className={cn("flex items-center space-x-6 lg:space-x-8", className)}>
        <div className="flex items-center space-x-2">
          {!hidePageSizeSelector && (
            <Select
              value={`${displayPageSize}`}
              onValueChange={(value) => {
                if (isUserInitiatedPaginationChanges)
                  isUserInitiatedPaginationChanges.current = true;
                table.setPageSize(Number(value));
                table.setPageIndex(0);
              }}
            >
              <SelectTrigger
                chevronClassName="hidden"
                className="h-8 rounded-[8px] bg-white hover:bg-hover-gray flex items-center border-0 squircle squircle-outline-[1px] squircle-outline-color-border-gray"
              >
                <p className="text-[11px] font-normal text-[#67749B] mr-1">
                  {paginationTitle ? paginationTitle : "Groups per page"}
                  {": "}
                </p>
                <span className="text-[11px] font-medium mr-1 text-brand-primary-new/80">
                  {displayPageSize}
                </span>
                <RiArrowDownSLine size={14} color="#9aa3bd" className="shrink-0" />
              </SelectTrigger>
              <SelectContent side="top" className="rounded-[8px]">
                {pageSizeOptions.map((pageSize) => (
                  <SelectItem
                    key={pageSize}
                    value={`${pageSize}`}
                    className="rounded-[4px] text-[#35467A] data-[state=checked]:font-medium [&>span:first-child]:data-[state=checked]:text-[#FF4500]"
                  >
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex items-center gap-[6px]">
          {/* Previous page button */}
          <Button
            variant="sq-outline"
            className="h-8 w-8 p-0 bg-white hover:bg-hover-gray transition-opacity duration-200"
            onClick={() => navigateToPage(displayedPage - 1)}
            disabled={displayedPage <= 1}
          >
            <span className="sr-only">Go to previous page</span>
            <RiArrowLeftSLine className="h-4 w-4" color="#9aa3bd" />
          </Button>

          {/* Page number buttons */}
          <AnimatePresence mode="popLayout">
            {visiblePages.map((item, index) =>
              item === "ellipsis" ? (
                <motion.span
                  key={`ellipsis-${index}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15 }}
                  className="flex h-8 w-8 items-center justify-center text-[12px] text-[#67749B] select-none"
                >
                  &hellip;
                </motion.span>
              ) : (
                <motion.button
                  key={item}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => navigateToPage(item)}
                  className={cn(
                    "relative h-8 w-8 rounded-[8px] text-[12px] transition-colors duration-200 ease-in-out",
                    item === displayedPage
                      ? "text-[#FF4500] font-medium"
                      : "text-[#35467A] font-normal hover:bg-hover-gray",
                  )}
                >
                  {item === displayedPage && (
                    <motion.div
                      layoutId="active-page-bg"
                      className="absolute inset-0 rounded-[8px] bg-[#FFECE4]"
                      transition={{ type: "spring", stiffness: 350, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{item}</span>
                </motion.button>
              ),
            )}
          </AnimatePresence>

          {/* Next page button */}
          <Button
            variant="sq-outline"
            className="h-8 w-8 p-0 bg-white hover:bg-hover-gray transition-opacity duration-200"
            onClick={() => navigateToPage(displayedPage + 1)}
            disabled={displayedPage >= totalPages}
          >
            <span className="sr-only">Go to next page</span>
            <RiArrowRightSLine className="h-4 w-4" color="#9aa3bd" />
          </Button>
        </div>
      </div>
    </div>
  );
}
